'use client';

import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useMemo, type DOMAttributes, type ReactNode } from 'react';
import { useEnvironmentInfo, useGoBack, useShareDocument } from '~/hooks';
import { IcClose, IcHome, IcSearch, IcShare, Typography } from '@bgzt/ui';
import { goToFullWebviewOnApp, goToHomeOnApp } from '~/services/app-interface';
import { utilStyles } from '~/styles';
import styles from './AppBar.module.scss';
import { BackButton } from './BackButton';
import { CartButton } from './CartButton';

type ActionName = 'home' | 'search' | 'share' | 'close';

type ActionConfig = DOMAttributes<HTMLButtonElement> & {
  name: ActionName | string;
  label?: string;
  icon?: ReactNode;
};

type Action = ActionName | ActionConfig;

type Props = {
  /** 왼쪽 영역에 있는 뒤로가기 버튼 (기본값: `true`) */
  back?: boolean | Omit<ActionConfig, 'name'>;
  /** 오른쪽 영역에 있는 장바구니 버튼 (기본값: `false`) */
  cart?: boolean | Omit<ActionConfig, 'name'>;
  close?: boolean | Omit<ActionConfig, 'name'>;
  /* 페이지 타이틀 (폰트 스타일 고정, 스타일에 대해 커스텀이 필요할 경우 slot 사용) */
  title?: string;
  /** 커스텀이 필요할 경우 (ex. 검색 페이지에서 input이 들어가야 하는 경우) */
  slot?: ReactNode;
  /**
   * 오른쪽 영역의 홈 이동, 검색, 공유하기 등의 CTA 버튼들
   * @description `${ActionName}`만 있을 경우 해당 액션 버튼 활성화 + 클릭시 기본 동작, `ActionConfig`를 넣을 경우 해당 액션 버튼 활성화 + config 대로 동작
   * - `home`: 홈으로 이동 버튼 (기본 동작: 홈으로 이동)
   * - `search`: 검색하기 버튼 (기본 동작: 검색 페이지로 이동)
   * - `share`: 공유하기 버튼 (기본 동작: 현재 페이지 공유)
   * - `${string}`: 커스텀 버튼
   */
  actions?: Action | Action[];
};

/**
 * 유저가 원하는 곳으로 빠르게 이동할 수 있도록 구조 정리
 *
 * @see {@link https://www.figma.com/file/VSu7tbh7a138GUwsWUE1HQ/%EB%A9%94%EB%A3%A8%EC%B9%B4%EB%A6%AC-%EC%83%81%ED%92%88%EC%9D%98-%EB%B2%88%EA%B0%9C%EC%9E%A5%ED%84%B0-%EC%97%B0%EB%8F%99-%EB%85%BC%EC%9D%98?type=design&node-id=3649-5294&mode=dev 디자인가이드}
 */
export const AppBar = ({ back = true, cart = false, actions, title, slot }: Props) => {
  const router = useRouter();
  const { isWebview } = useEnvironmentInfo();
  const share = useShareDocument();
  const { goBack } = useGoBack();

  const defaultActions: Record<ActionName, ActionConfig> = useMemo(() => {
    return {
      home: {
        name: 'home',
        label: '홈으로',
        icon: <IcHome size={20} />,
        onClick: () => (isWebview ? goToHomeOnApp().go() : router.push('/')),
      },
      search: {
        name: 'search',
        label: '검색하기',
        icon: <IcSearch size={20} />,
        onClick: () =>
          isWebview
            ? goToFullWebviewOnApp({ url: `${process.env.NEXT_PUBLIC_DOMAIN}${'/search'}` }).go()
            : globalThis.window.open('/search', '_blank'),
      },
      share: {
        name: 'share',
        label: '공유하기',
        icon: <IcShare size={20} />,
        onClick: () => share({ title: globalThis.window.document.title }),
      },
      close: {
        name: 'close',
        label: '닫기',
        icon: <IcClose size={20} />,
        onClick: () => {
          goBack({ backType: 'close' });
        },
      },
    };
  }, [isWebview, router, share, goBack]);

  return (
    <header className={clsx([styles.header])}>
      <div>
        {/* 뒤로가기 */}
        {back && <BackButton config={back} />}
        {/* 페이지 제목 */}
        {title && (
          <Typography variant="S1" as="h1" color="gray-900">
            {title}
          </Typography>
        )}
        {/* 커스텀 영역 */}
        {slot}
        <div className={utilStyles.mrAuto} />
        {actions &&
          [actions].flat().map((action) => {
            const { name, label, icon, ...config } =
              typeof action === 'string'
                ? defaultActions[action]
                : Object.keys(defaultActions).includes(action.name)
                  ? { ...defaultActions[action.name as ActionName], ...action }
                  : action;

            return (
              <button key={name} {...config}>
                <span className={utilStyles.visuallyHidden}>{label || name}</span>
                {icon}
              </button>
            );
          })}
        {/* 장바구니 */}
        {cart && <CartButton config={cart} />}
      </div>
    </header>
  );
};
